// src/screens/Marketplace/ProductDetailScreen.js

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';
import './ProductDetailScreen.css';
import { useCart } from '../../CartContext';
import ProductSkeleton from '../../components/skeleton/ProductSkeleton';
import ReviewPopup from '../../components/popups/ReviewPopup';
import { useMarketContext } from '../../context/MarketContext';
import { useMerchantStore } from '../../context/MerchantStoreContext';
import { useLikedProducts } from '../../context/LikedProductsContext';
import { useSavedProducts } from '../../context/SavedProductsContext';

import backArrowIcon from '../../assets/backarrow-icon.png';
import likeIcon from '../../assets/like-icon.png';
import likeIconRed from '../../assets/like-icon-red.png';
import saveIcon from '../../assets/saved-icon.png';
import saveIconFilled from '../../assets/save-icon-filled.png';
import infoIcon from '../../assets/info-icon.png';
import securedIcon from '../../assets/secured-icon.png';
import reportIcon from '../../assets/report-icon.png';
import ReportPopup from '../../components/popups/ReportPopup';
import ReviewListDrawer from '../../components/popups/ReviewListDrawer';
import ReadMoreText from '../../common/ReadMoreText';

import shareIcon from '../../assets/share-icon.png';
import ProductShareDrawer from '../../components/popups/ProductShareDrawer';

// ---------- helpers (same behavior as Department/Banner/Suggested) ----------
function computePricingView(p, piToUsdRate) {
  // 1) Prefer server-enriched pricingView
  if (p?.pricingView && typeof p.pricingView.basePrice === 'number') {
    const base = +Number(p.pricingView.basePrice).toFixed(2);
    const eff =
      typeof p.pricingView.effectivePrice === 'number'
        ? +Number(p.pricingView.effectivePrice).toFixed(2)
        : base;
    const isOnSale = !!p.pricingView.isOnFlashSale && eff < base;
    const percent = isOnSale && base > 0 ? Math.max(0, Math.round((1 - eff / base) * 100)) : null;
    return { basePi: base, salePi: isOnSale ? eff : null, percentOff: percent, isOnSale };
  }

  // 2) Next: legacy effectivePricePi fields if ever present
  if (typeof p?.effectivePricePi === 'number') {
    const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
    const base =
      isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
        ? +Number(p.usdPrice / piToUsdRate).toFixed(2)
        : typeof p?.price === 'number'
        ? +Number(p.price).toFixed(2)
        : null;
    const eff = +Number(p.effectivePricePi).toFixed(2);
    if (base != null) {
      const isOnSale = !!(p?.sale?.active || p?.isOnSale === true) && eff < base;
      const percent = isOnSale && base > 0 ? Math.max(0, Math.round((1 - eff / base) * 100)) : null;
      return { basePi: base, salePi: isOnSale ? eff : null, percentOff: percent, isOnSale };
    }
  }

  // 3) Fallback compute from raw fields
  const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
  const basePi =
    isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? +Number(p.usdPrice / piToUsdRate).toFixed(2)
      : typeof p?.price === 'number'
      ? +Number(p.price).toFixed(2)
      : null;

  const sale = p?.sale || p?.flashSale || p?.activeSale || {};
  const saleActive =
    !!sale?.active ||
    typeof p?.activeSalePricePi === 'number' ||
    typeof p?.activeSalePriceUsd === 'number' ||
    typeof p?.salePrice === 'number' ||
    typeof p?.salePricePi === 'number' ||
    typeof p?.salePriceUsd === 'number';

  if (!saleActive || basePi == null) return { basePi, salePi: null, percentOff: null, isOnSale: false };

  let salePi = null;
  if (typeof sale?.pricePi === 'number') salePi = sale.pricePi;
  else if (typeof p?.activeSalePricePi === 'number') salePi = p.activeSalePricePi;
  else if (typeof p?.salePricePi === 'number') salePi = p.salePricePi;

  const usdToPi = (usd) =>
    typeof usd === 'number' && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? usd / piToUsdRate
      : null;

  if (salePi == null) {
    if (typeof sale?.priceUsd === 'number') salePi = usdToPi(sale.priceUsd);
    else if (typeof p?.activeSalePriceUsd === 'number') salePi = usdToPi(p.activeSalePriceUsd);
    else if (typeof p?.salePriceUsd === 'number') salePi = usdToPi(p.salePriceUsd);
  }
  if (salePi == null && typeof p?.salePrice === 'number') salePi = p.salePrice;

  if (salePi == null) return { basePi, salePi: null, percentOff: null, isOnSale: false };

  const finalSalePi = Math.max(0, +Number(salePi).toFixed(2));
  const percentOff = Math.max(0, Math.min(100, Math.round((1 - finalSalePi / basePi) * 100)));
  const isOnSale = finalSalePi < basePi;

  return { basePi, salePi: isOnSale ? finalSalePi : null, percentOff: isOnSale ? percentOff : null, isOnSale };
}
// ---------------------------------------------------------------------------

// ⏳ lightweight 1s countdown; auto-cleans on unmount
function useSaleCountdown(isoEnd) {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!isoEnd) return;
    const endMs = new Date(isoEnd).getTime();
    if (!Number.isFinite(endMs)) return;

    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isoEnd]);

  const endMs = isoEnd ? new Date(isoEnd).getTime() : NaN;
  const remaining = Number.isFinite(endMs) ? Math.max(0, endMs - now) : 0;
  const over = remaining <= 0;

  const s = Math.floor(remaining / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  return { over, d, h, m, sec };
}

function ProductDetailScreen({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCart = location.state?.fromCart || false;
  const scrollRef = useRef();

  const [product, setProduct] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { savedProductIds, toggleSave } = useSavedProducts();
  const saved = savedProductIds.includes(product?._id);

  const [localLikeCount, setLocalLikeCount] = useState(0);
  const [showDynamicInfo, setShowDynamicInfo] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showEscrowInfo, setShowEscrowInfo] = useState(false);

  const { addToCart } = useCart();

  const [showReportPopup, setShowReportPopup] = useState(false);

  // screen-only toast for product report (success | error)
  const [reportToast, setReportToast] = useState({ visible: false, message: '', type: '' });
  const showReportToast = (message, type = 'success', duration = 2200) => {
    setReportToast({ visible: true, message, type });
    window.clearTimeout(showReportToast._t);
    showReportToast._t = window.setTimeout(() => {
      setReportToast({ visible: false, message: '', type: '' });
    }, duration);
  };

  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [averageRating, setAverageRating] = useState(null);
  const [showReviewDrawer, setShowReviewDrawer] = useState(false);
  const [piToUsdRate, setPiToUsdRate] = useState(null);

  const { likedProducts, toggleLike, setLikedProducts } = useLikedProducts();
  const liked = likedProducts.includes(product?._id);


const [showProductShare, setShowProductShare] = useState(false);

const openShare = () => {
  if (!user?.username) return;
  setShowProductShare(true);
};

const closeShare = () => setShowProductShare(false);


  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${id}?username=${user?.username}`);
        const fetchedProduct = res.data.product;

        setProduct(fetchedProduct);
        setLocalLikeCount(fetchedProduct.likes?.length || 0);

        if (fetchedProduct.userHasLiked) {
          setLikedProducts((prev) =>
            prev.includes(fetchedProduct._id) ? prev : [...prev, fetchedProduct._id]
          );
        }

        try {
          const avgRes = await api.get(`/reviews/average/${id}`);
          if (avgRes.data?.average !== undefined) {
            setAverageRating(avgRes.data.average.toFixed(1));
          }
        } catch (err) {
          console.error('❌ Failed to fetch average rating:', err);
        }
      } catch (err) {
        console.error('❌ Failed to fetch product:', err);
      }
    };

    fetchProduct();
  }, [id, user?.username, setLikedProducts]);

  useEffect(() => {
    let hasTracked = false;

    const trackClick = async () => {
      if (hasTracked || !id || !user?.username) return;
      try {
        await api.post(`/products/track-click/${id}`);
        hasTracked = true;
      } catch (err) {
        console.error('❌ Failed to track product click:', err);
      }
    };

    trackClick();
  }, [id, user?.username]);

  useEffect(() => {
    const fetchPiRate = async () => {
      try {
        const res = await api.get('/convert/rate');
        if (res.data?.piToUsd) setPiToUsdRate(res.data.piToUsd);
      } catch (err) {
        console.error('❌ Failed to fetch cached Pi rate:', err.message);
      }
    };
    fetchPiRate();
    const interval = setInterval(fetchPiRate, 600000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkIfReviewNeeded = async () => {
      try {
        if (!product || !user?.username || user.username === product.owner) return;

        const possible = await api.get(`/escrow/buyer/${user.username}`);
        const escrows = possible.data;
        const match = escrows.find((e) => e.productId === product._id);
        if (!match) return;

        const res = await api.get(`/escrow/by-user-product/${user.username}/${id}`);
        const escrow = res.data;

        if (escrow?.status === 'completed' && !escrow?.hasReviewed) {
          setTimeout(() => setShowReviewPopup(true), 1000);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('❌ Review popup check failed:', err);
        }
      }
    };

    checkIfReviewNeeded();
  }, [user?.username, id, product]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !el.firstChild) return;
    const scrollLeft = el.scrollLeft;
    const childWidth = el.firstChild.offsetWidth + 10;
    const index = Math.round(scrollLeft / childWidth);
    setActiveIndex(index);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const isOwner = user?.username === product?.owner?.username;

  const handleToggleLike = () => {
    if (!user || !product?._id) return;
    toggleLike(product._id, api, user.username);
    setLocalLikeCount((prev) => (liked ? Math.max(prev - 1, 0) : prev + 1));
  };

  const handleToggleSave = () => {
    if (!product?._id) return;
    toggleSave(product._id, user?.username);
  };

  const handleAddToCart = async () => {
    try {
      await addToCart(product._id);
      setToastType('success');
      setToastMessage('Product added to cart!');
      setTimeout(() => setToastMessage(''), 2500);
    } catch (err) {
      console.error('❌ Failed to add to cart:', err);
      const alreadyAdded = err.response?.data?.message?.includes('already');
      setToastType('error');
      setToastMessage(alreadyAdded ? 'Product already in cart!' : 'Something went wrong. Try again.');
      setTimeout(() => setToastMessage(''), 2500);
    }
  };

  // ---- Countdown HOOK must be called on every render (even when product is null) ----
  const saleEndsAt =
    product?.pricingView?.saleEndsAt ||
    product?.pricing?.saleEndsAt ||
    product?.sale?.endAt ||
    null;

  const { over, d, h, m, sec } = useSaleCountdown(saleEndsAt);
  // -------------------------------------------------------------------------------

  if (!product) return <ProductSkeleton />;

  // Normalize delivery methods from API (supports old + new shapes)
  const deliveryMethods = Array.isArray(product.deliveryMethods)
    ? product.deliveryMethods
    : Array.isArray(product.deliveryMethod)
    ? product.deliveryMethod
    : typeof product.deliveryMethod === 'string' && product.deliveryMethod.trim()
    ? [product.deliveryMethod.trim()]
    : typeof product.customOtherDelivery === 'string' && product.customOtherDelivery.trim()
    ? [product.customOtherDelivery.trim()]
    : [];

  const getDescriptionLabel = () =>
    product.category === 'service' ? 'Service Description' : 'Product Description';

  const getFullCategory = () => {
    switch (product.category) {
      case 'physical':
        return 'Physical Product';
      case 'digital':
        return 'Digital Product';
      case 'barter':
        return 'Barter Offer';
      case 'service':
        return 'Service';
      default:
        return product.category;
    }
  };

  // ✅ Compute price view (original + sale if active) — after the early return
  const { basePi, salePi, percentOff, isOnSale } = computePricingView(product, piToUsdRate);

  return (
    <div className="productdetail-container">
      <div className="productdetail-header">
        <img
          src={backArrowIcon}
          alt="Back"
          className="productdetail-back-icon"
          onClick={() => navigate(-1)}
        />

        <h2 className="productdetail-title" title={product.name}>
          {product.name}
        </h2>
      </div>

      <div className="productdetail-scroll">
        <div className="product-carousel-wrapper">
          <div className="product-carousel-scroll" ref={scrollRef} onScroll={handleScroll}>
            {product.media?.map((url, i) => (
              <img
                key={i}
                src={url || 'https://via.placeholder.com/300'}
                onError={(e) => (e.target.src = 'https://via.placeholder.com/300')}
                alt={`product-${i}`}
                className="product-carousel-img"
              />
            ))}
          </div>

          {product.media?.length > 1 && (
            <div className="product-carousel-dots">
              {product.media.map((_, index) => (
                <span key={index} className={`product-dot ${activeIndex === index ? 'active' : ''}`} />
              ))}
            </div>
          )}
        </div>

        <div className="productdetail-info">


<div className="productdetail-info-actions">
  {/* Like */}
  <button className="pd-action-btn" onClick={handleToggleLike} type="button">
    <img
      src={liked ? likeIconRed : likeIcon}
      alt="Like"
      className={`pd-action-icon ${liked ? 'pop-liked' : ''}`}
    />
    <span className="pd-action-count" style={{ opacity: localLikeCount > 0 ? 1 : 0 }}>
      {localLikeCount}
    </span>
  </button>

  {/* Share */}
  <button className="pd-action-btn" onClick={openShare} type="button">
    <img src={shareIcon} alt="Share" className="pd-action-icon pd-action-icon--small" />
  </button>

  {/* Save */}
  <button className="pd-action-btn" onClick={handleToggleSave} type="button">
    <img
      src={saved ? saveIconFilled : saveIcon}
      alt="Save"
      className="pd-action-icon pd-action-icon--small"
    />
  </button>

  {/* Report */}
  <button className="pd-action-btn" onClick={() => setShowReportPopup(true)} type="button">
    <img
      src={reportIcon}
      alt="Report"
      className="pd-action-icon pd-action-icon--small"
    />
  </button>
</div>


          <p className="productdetail-section-title review-clickable" onClick={() => setShowReviewDrawer(true)}>
            {averageRating ? `Reviews (★ ${averageRating})` : 'See Reviews'}
          </p>

          <p className="productdetail-section-title">{getDescriptionLabel()}:</p>
          <ReadMoreText text={product.description} maxLines={3} className="productdetail-description" />

          {/* Category */}
          <p className="productdetail-section-title">Category:</p>
          <p className="productdetail-category">{getFullCategory()}</p>

          {/* Condition */}
          {product?.condition && (
            <>
              <p className="productdetail-section-title">Condition:</p>
              <p className="productdetail-category">
                <span className={`pd-badge ${product.condition === 'used' ? 'used' : 'new'}`}>
                  {product.condition === 'used' ? 'Used' : 'New'}
                </span>
              </p>
            </>
          )}

          {/* Estimated Delivery */}
          {product.expectedDeliveryDays && (
            <>
              <p className="productdetail-section-title">Estimated Delivery:</p>
              <p className="productdetail-category">
                {product.expectedDeliveryDays} day{product.expectedDeliveryDays > 1 ? 's' : ''}
              </p>
            </>
          )}

          {/* Delivery Methods */}
          {deliveryMethods.length > 0 && (
            <>
              <p className="productdetail-section-title">
                Delivery Method{deliveryMethods.length > 1 ? 's' : ''}:
              </p>
              <p className="productdetail-category">
                {deliveryMethods.map((m, i) => (
                  <span key={`${m}-${i}`} className="pd-badge" style={{ marginRight: 6, marginBottom: 6 }}>
                    {m}
                  </span>
                ))}
              </p>
            </>
          )}

          {/* Price block */}

{/* Label + sale badge */}
<div className="productdetail-price-label">
  <p className="productdetail-section-title">Price:</p>
  {isOnSale && typeof percentOff === 'number' && percentOff > 0 && (
    <span className="pdp-percent-pill label">-{percentOff}%</span>
  )}
</div>

{/* One-line: original (strike) + sale (NO %-pill here) */}
<div className="pdp-price-row">
  {isOnSale && salePi != null ? (
    <>
      <span className="pdp-price-original">{basePi.toFixed(2)} Pi</span>
      <span className="pdp-price-sale">{salePi.toFixed(2)} Pi</span>
    </>
  ) : (
    <span className="pdp-price-regular">
      {basePi != null ? `${basePi.toFixed(2)} Pi` : '—'}
    </span>
  )}
</div>


          {/* Countdown directly under the price row (red) */}
          {isOnSale && saleEndsAt && !over && (
            <div className="pdp-offer-ends">
              Offer ends in&nbsp;
              <strong className="pdp-countdown">
                {d}d {String(h).padStart(2, '0')}h {String(m).padStart(2, '0')}m {String(sec).padStart(2, '0')}s
              </strong>
            </div>
          )}


          {product.pricingType === 'dynamic' && (
            <div className="productdetail-dynamic-label">
              <span>Dynamic</span>
              <img
                src={infoIcon}
                alt="Info"
                className="productdetail-info-icon"
                onClick={() => setShowDynamicInfo(true)}
              />
            </div>
          )}

          {!isOwner && (
            <div className="productdetail-button-row">
              <button
                className="productdetail-chat-button"
                onClick={() => navigate(`/chat/${product.owner?.username}`)}
              >
                Chat with Seller
              </button>

              <button
                className="productdetail-store-button"
                onClick={() => {
                  navigate(`/marketplace/store/${product.owner?.businessUsername}`);
                }}
              >
                Visit Store
              </button>
            </div>
          )}

          {!isOwner && (
            <div className="productdetail-escrow-label" onClick={() => setShowEscrowInfo(true)}>
              <img src={securedIcon} alt="Escrow Secured" className="secured-icon" />
              <span>Secured by escrow payment</span>
            </div>
          )}
        </div>
      </div>

      <div className="productdetail-bottom">
        {isOwner ? (
          <button className="productdetail-action-btn" onClick={() => navigate(`/edit-product/${product._id}`)}>
            Edit Product
          </button>
        ) : fromCart ? (
          user?.status === 'suspended' ? (
            <>
              <button className="productdetail-action-btn disabled" disabled>
                🚫 Account Suspended
              </button>
              <button className="productdetail-action-btn appeal-btn" onClick={() => navigate('/appeal')}>
                Appeal
              </button>
            </>
          ) : (
            <button className="productdetail-action-btn" onClick={() => navigate(`/checkout/${product._id}`)}>
              Proceed to Checkout
            </button>
          )
        ) : (
          <button className="productdetail-action-btn" onClick={handleAddToCart}>
            Add to Cart
          </button>
        )}
      </div>

      {showDynamicInfo && (
        <div className="dynamic-popup-backdrop" onClick={() => setShowDynamicInfo(false)}>
          <div className="dynamic-popup-content" onClick={(e) => e.stopPropagation()}>
            <h3>Dynamic Pricing</h3>
            <p>
              This product uses <strong>dynamic pricing</strong>. The Pi amount is based on a fixed USD price set by the
              merchant, and updates every 10 minutes to reflect the latest Pi-to-USD rate.
            </p>
            <button onClick={() => setShowDynamicInfo(false)}>Close</button>
          </div>
        </div>
      )}

      {showSuccess && <div className="slide-success-banner">Product added to cart!</div>}

      {toastMessage && <div className={`slide-toast-banner ${toastType}`}>{toastMessage}</div>}

      {showEscrowInfo && (
        <div className="escrow-drawer-backdrop" onClick={() => setShowEscrowInfo(false)}>
          <div className="escrow-drawer" onClick={(e) => e.stopPropagation()}>
            <h3>What is Escrow?</h3>
            <p>
              BarterFi protects both buyers and sellers using a secure escrow system. When a buyer places an order, the
              Pi is held safely in escrow and only released once both sides confirm delivery. If there's no response
              from the seller in 48 hours, the order is automatically cancelled.
            </p>
            <p>
              Once the seller confirms, the buyer has{' '}
              <strong>
                {product?.expectedDeliveryDays || 7} day{product?.expectedDeliveryDays > 1 ? 's' : ''}
              </strong>{' '}
              to confirm receipt. If they don't, the system auto-releases the funds to the seller.
            </p>
            <p>
              In case of disputes, the transaction is paused and our team will review it for a fair resolution. Refunds
              are issued when appropriate.
            </p>
            <button className="escrow-drawer-close-btn" onClick={() => setShowEscrowInfo(false)}>
              Got it
            </button>
          </div>
        </div>
      )}

      {showReportPopup && (
        <ReportPopup
          onClose={() => setShowReportPopup(false)}
          onValidationError={(msg) => showReportToast(msg || 'Please select a reason.', 'error')}
          onSubmit={async (payload) => {
            try {
              await api.post('/reports', payload);
              setShowReportPopup(false);
              showReportToast(' Report submitted successfully!', 'success');
            } catch (err) {
              console.error('❌ Report failed:', err);
              setShowReportPopup(false);
              showReportToast('❌ Failed to submit report. Please try again.', 'error');
            }
          }}
          reportedId={product._id}
          reporterUsername={user?.username}
          type="product"
        />
      )}


<ProductShareDrawer
  isOpen={showProductShare}
  onClose={closeShare}
  product={product}
  sender={user?.username}
/>


      {showReviewPopup && (
        <ReviewPopup
          productId={product._id}
          buyerUsername={user.username}
          onClose={() => setShowReviewPopup(false)}
          onSubmitSuccess={() => {
            setShowReviewPopup(false);
            alert('✅ Review submitted!');
          }}
        />
      )}

      <ReviewListDrawer productId={product._id} isOpen={showReviewDrawer} onClose={() => setShowReviewDrawer(false)} />

      {reportToast.visible && <div className={`product-toast ${reportToast.type}`}>{reportToast.message}</div>}
    </div>
  );
}

export default ProductDetailScreen;
